import { useCallback, useMemo, useState } from "react";
import { useIonRouter } from "@ionic/react";

import { Icon } from "@france/superelements/lib";

import ROUTES from "../../../constants/routes";
import ProposalDraftModal from "../../../../components/ProposalDraftModal/ProposalDraftModal";
import useClearProposal from "../../../../redux/slices/proposal/hooks/useClearProposal";
import { useLocalize } from "@france/superelements/utils";
import ChoiceModal from "../../../../components/ChoiceModal/ChoiceModal";
import { useGetLastAcceptedSherpaConditionsQuery } from "../../../../gql/hooks";
import useCPVProposal from "../../../../redux/slices/proposal/hooks/useCPVProposal";

const NewProposalButton: React.FC<unknown> = () => {
  const t = useLocalize();
  const { push, routeInfo } = useIonRouter();
  const clearProposalState = useClearProposal();

  // Get CPVS if in POS
  const posId = useMemo(() => {
    if (
      routeInfo.pathname.startsWith(ROUTES.POS) &&
      !!routeInfo.pathname.replace(ROUTES.POS, "").replace("/", "")
    ) {
      return Number(
        routeInfo.pathname.replace(ROUTES.POS, "").replace("/", ""),
      );
    }
  }, [routeInfo]);

  const { createProposalWithCPVs } = useCPVProposal(posId);

  const { data: cpvData } = useGetLastAcceptedSherpaConditionsQuery({
    skip: !posId,
    variables: {
      posId: posId || 0,
    },
    fetchPolicy: "network-only",
  });

  const containsCPVs = useMemo(
    () => !!cpvData?.lastAcceptedSherpaConditions?.length,
    [cpvData],
  );

  // States
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [openDraftModal, setDraftModal] = useState(false);

  // handlers
  const handleCreateProposal = useCallback(() => setDraftModal(true), []);
  const handleCloseProposal = useCallback(() => setDraftModal(false), []);

  const handleOpenCreates = useCallback(() => setCreateOpen(true), []);
  const handleCloseCreates = useCallback(() => setCreateOpen(false), []);
  const handleCreateProposalWithoutCPV = useCallback(() => {
    handleCloseCreates();
    handleCreateProposal();
  }, [handleCloseCreates, handleCreateProposal]);
  const handleCreateProposalWithCPV = useCallback(() => {
    createProposalWithCPVs();
    handleCloseCreates();
  }, [createProposalWithCPVs, handleCloseCreates]);

  // Hanlders
  const handleGoPosCP = useCallback(() => {
    if (
      routeInfo.pathname.startsWith(ROUTES.POS) &&
      !!routeInfo.pathname.replace(ROUTES.POS, "").replace("/", "")
    ) {
      if (containsCPVs) handleOpenCreates();
      else handleCreateProposal();
    } else {
      clearProposalState();
      push(ROUTES.SELECT_POS);
    }
  }, [
    push,
    handleCreateProposal,
    clearProposalState,
    handleOpenCreates,
    containsCPVs,
    routeInfo.pathname,
  ]);

  return (
    <>
      <ProposalDraftModal
        posId={posId}
        openStatus={openDraftModal}
        onClose={handleCloseProposal}
        redirect
      />
      <ChoiceModal
        title={t("proposal.cpv.create")}
        isOpen={isCreateOpen}
        onClose={handleCloseCreates}
        onFirst={handleCreateProposalWithoutCPV}
        onSecond={handleCreateProposalWithCPV}
        firstLabel={t("proposal.cpv.createWithoutCPV")}
        secondLabel={t("proposal.cpv.createWithCPV")}
      />
      <button className="ButtonHighlight primary " onClick={handleGoPosCP}>
        <Icon icon="plus-square" />
      </button>
    </>
  );
};

export default NewProposalButton;
